import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { router } from '../../utils/router.js'
import { authService } from '../../services/auth/auth-service.js'
import { claimInvite } from '../../services/teams/teams-api.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/outlined-button.js'
import '@material/web/progress/circular-progress.js'

@customElement('cairn-team-invite-claim')
export class CairnTeamInviteClaim extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      background: var(--cairn-background-color, #f8fafc);
      align-items: center;
      justify-content: center;
    }

    .card {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-xl, 32px);
      max-width: 400px;
      width: 90%;
      text-align: center;
    }

    .icon {
      font-size: 48px;
      margin-bottom: var(--cairn-spacing-md, 16px);
      font-family: 'Material Symbols Outlined';
    }

    .icon.success {
      color: #16a34a;
    }
    .icon.error {
      color: #dc2626;
    }
    .icon.loading {
      color: var(--cairn-primary-color, #3b82f6);
    }

    h2 {
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 8px;
    }

    p {
      color: var(--cairn-text-secondary-color, #64748b);
      margin: 0 0 24px;
    }

    .actions {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      justify-content: center;
    }
  `

  @property({ type: String }) token = ''

  @state() private status: 'loading' | 'success' | 'error' | 'unauthenticated' = 'loading'
  @state() private entityType: 'team' | 'plan' = 'team'
  @state() private entityName = ''
  @state() private entityId = ''
  @state() private errorMessage = ''

  override connectedCallback(): void {
    super.connectedCallback()
    this.detectEntityType()
    void this.processInvite()
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('token') && this.token) {
      this.detectEntityType()
      void this.processInvite()
    }
  }

  private detectEntityType(): void {
    const path = window.location.pathname
    if (path.includes('/plans/invite/')) {
      this.entityType = 'plan'
    } else {
      this.entityType = 'team'
    }
  }

  private async processInvite(): Promise<void> {
    if (!this.token) return

    if (authService.getState().status !== 'authenticated') {
      sessionStorage.setItem('pendingInviteToken', this.token)
      this.status = 'unauthenticated'
      return
    }

    this.status = 'loading'

    try {
      const result = await claimInvite(this.token)
      if (result.success) {
        this.status = 'success'
        this.entityType = result.entityType ?? this.entityType
        this.entityName = result.entityName ?? result.teamName ?? result.planName ?? ''
        this.entityId = result.entityId ?? result.teamId ?? result.planId ?? ''
      } else {
        this.status = 'error'
        this.errorMessage = result.error ?? 'Failed to join'
      }
    } catch (err) {
      this.status = 'error'
      this.errorMessage = err instanceof Error ? err.message : 'Something went wrong'
    }
  }

  private get entityLabel(): string {
    return this.entityType === 'plan' ? 'plan' : 'team'
  }

  private get listPath(): string {
    return this.entityType === 'plan' ? '/plans' : '/teams'
  }

  private get detailPath(): string {
    return `${this.listPath}/${this.entityId}`
  }

  override render(): unknown {
    return html`
      <div class="card">
        ${this.status === 'loading' ? this.renderLoading() : ''}
        ${this.status === 'success' ? this.renderSuccess() : ''}
        ${this.status === 'error' ? this.renderError() : ''}
        ${this.status === 'unauthenticated' ? this.renderUnauthenticated() : ''}
      </div>
    `
  }

  private renderLoading(): unknown {
    return html`
      <div class="icon loading">
        <md-circular-progress indeterminate></md-circular-progress>
      </div>
      <h2>Joining ${this.entityLabel}...</h2>
      <p>Please wait while we process your invitation.</p>
    `
  }

  private renderSuccess(): unknown {
    return html`
      <div class="icon success">check_circle</div>
      <h2>Welcome to ${this.entityName}!</h2>
      <p>You've successfully joined the ${this.entityLabel}.</p>
      <div class="actions">
        <md-filled-button @click=${() => router.navigate(this.detailPath)}>
          View ${this.entityType === 'plan' ? 'Plan' : 'Team'}
        </md-filled-button>
      </div>
    `
  }

  private renderError(): unknown {
    return html`
      <div class="icon error">error</div>
      <h2>Unable to join</h2>
      <p>${this.errorMessage}</p>
      <div class="actions">
        <md-filled-button @click=${() => router.navigate(this.listPath)}>
          Go to ${this.entityType === 'plan' ? 'Plans' : 'Teams'}
        </md-filled-button>
      </div>
    `
  }

  private renderUnauthenticated(): unknown {
    return html`
      <div class="icon loading">group_add</div>
      <h2>Sign in to join</h2>
      <p>You need to sign in or create an account to accept this invitation.</p>
      <div class="actions">
        <md-filled-button @click=${() => router.navigate('/auth/login')}>
          Sign In
        </md-filled-button>
        <md-outlined-button @click=${() => router.navigate('/auth/register')}>
          Create Account
        </md-outlined-button>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-team-invite-claim': CairnTeamInviteClaim
  }
}
